import { HTMLProps, memo, MutableRefObject } from 'react';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { RecordAny } from '@dealstack/shared/lib/common/common.ts';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { BasicMantineTopToolbar } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTopToolbar.tsx';
import {
	MantineTableDataOperations,
	useMantineTableDataOperations,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { Row, Table } from '@tanstack/react-table';
import { BoxProps } from '@mantine/core';

interface Props<TData extends RecordAny> {
	columns: ExtendedColumnDef<TData>[];
	data: TData[];
	customActions?: JSX.Element | ((table: Table<TData>) => JSX.Element);
	isLoading?: boolean;
	hideTopToolbar?: boolean;
	hideSearchBar?: boolean;
	toolbarClassName?: string;
	dataOperations?: MantineTableDataOperations;
	manualFiltering?: boolean;
	showColumnFilters?: boolean;
	onShowColumnFiltersChange?: (value: boolean) => void;
	enableExpanding?: boolean;
	enableColumnResizing?: boolean;
	enableHiding?: boolean;
	mantineTableBodyRowProps?: (props: {
		isDetailPanel?: boolean;
		row: Row<TData>;
		staticRowIndex: number;
		table: Table<TData>;
	}) => Omit<
		HTMLProps<HTMLTableRowElement>,
		'data' | 'color' | 'size' | 'type' | 'ref' | 'label'
	> & {
		ref?: MutableRefObject<HTMLTableRowElement | null> | null | undefined;
	} & BoxProps;
	renderDetailPanel?: (args: { row: Row<TData>; table: Table<TData> }) => React.ReactNode;
	mantineDetailPanelProps?: React.HTMLProps<HTMLDivElement> & BoxProps;
	getRowCanExpand?: (row: Row<TData>) => boolean;
	getRowId?: (row: TData, index: number, parent?: unknown) => string;
	dataTestId?: string;
	hideBorders?: boolean;
	enableVerticalCellBorders?: boolean;
}

const BasicMantineTable = <T extends RecordAny>({
	columns,
	data,
	customActions,
	isLoading,
	hideTopToolbar,
	hideSearchBar,
	toolbarClassName,
	dataOperations,
	manualFiltering = false,
	enableExpanding,
	mantineTableBodyRowProps,
	renderDetailPanel,
	mantineDetailPanelProps,
	getRowCanExpand,
	dataTestId,
	...restProps
}: Props<T>) => {
	const ownDataOperations = useMantineTableDataOperations();

	return (
		<MantineTable
			columns={columns}
			isLoading={isLoading}
			data={data ?? []}
			dataTestId={dataTestId}
			enablePagination={false}
			enableRowSelection={false}
			enableGlobalFilter={true}
			enableRowActions={false}
			enableFullScreenToggle={false}
			enableHiding={false}
			manualFiltering={manualFiltering}
			manualSorting={false}
			enableExpanding={enableExpanding}
			dataOperations={dataOperations ?? ownDataOperations}
			mantineTableBodyRowProps={mantineTableBodyRowProps}
			renderDetailPanel={renderDetailPanel}
			mantineDetailPanelProps={mantineDetailPanelProps}
			getRowCanExpand={getRowCanExpand}
			renderTopToolbar={({ table }) =>
				hideTopToolbar ? null : (
					<BasicMantineTopToolbar
						table={table}
						className={toolbarClassName}
						hideSearchBar={hideSearchBar}
						customActions={
							typeof customActions === 'function'
								? customActions(table)
								: customActions
						}
					/>
				)
			}
			{...restProps}
		/>
	);
};

const Memoized = <T extends RecordAny>(props: Props<T>) => <BasicMantineTable {...props} />;
const _BasicMantineTableExport = memo(Memoized) as typeof Memoized;

export { _BasicMantineTableExport as BasicMantineTable };
